// Eigener Bereich "Abwaegen" unter Vertraege (Festlegung vom 23.09.2026).
//
// Hier wird mit der KI durchgesprochen, was man einem Kunden auf einen Wunsch
// oder Einwand antworten kann - wirtschaftlich und mit Blick auf die
// Verkaeuflichkeit an Investoren. Bis zum 23.09. war das ein Reiter im
// Gespraechs-Panel des Vertragsstudios; dort fehlte der Platz, und das
// Abwaegen hing an einem geoeffneten Vertrag, obwohl man es oft vorher braucht.
//
// Zwei Unterschiede zum Entwerfen, beide gewollt:
//   - Der Agent sieht die GANZE Wissensbasis bei Pohl (Einwaende,
//     Richtigstellungen, Schulung) UND die Vertragsvorlagen. Beim Entwerfen
//     sieht er nur die Vorlagen.
//   - Ein Deal ist freiwillig. Ohne Deal gibt es eine persoenliche Sitzung,
//     deren Kennung der Server aus dem Benutzer ableitet. Mit Deal kommen die
//     Eckdaten aus Bitrix dazu, die Antwort kann als Kommentar an den Deal, und
//     fruehere Abwaegungen zu diesem Deal erscheinen wieder.
//
// Arbeitsweise seit 23.09.2026 (Festlegung des Nutzers): Links das Gespraech
// mit Text und Dateien, rechts die Auswertung mit den Reitern Offen,
// Uebernommen und Antwort (AbwaegenAuswertung.jsx). Jeder Punkt wird
// uebernommen oder kommentiert; die Kommentare gehen gesammelt an die KI, die
// nur das Offene ueberarbeitet.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Briefcase, FileUp, Paperclip, Scale, Send, X as XIcon } from 'lucide-react';
import { T, knopfStil, primaerKnopfStil, feldStil, markeStil } from '../../lib/designTokens';
// Adressen, Nutzlasten, Zaehler und Texte teilt sich diese Seite seit dem
// 23.09.2026 mit der mobilen Oberflaeche (src/lib/abwaegen.js).
import {
  ABWAEGEN_SCHRITTE, DEAL_ERKLAERUNG, LEERE_ABWAEGUNG, abwaegenAdresse, abwaegenKoerper,
  abwaegenSitzungsParameter, hinweisZumSenden, istDealNummer, mitPunktStand, nachrichtAngekommen,
  offeneKommentarEntwuerfe, unterwegsText, zaehleKommentare,
} from '../../lib/abwaegen';
import { erstelleKette } from '../../lib/aufrufKette';
import { DATEI_ANNAHME, dateiNutzlast } from '../../lib/kiDatei';
import { Ueberzeile } from './abwaegenAnzeige';
import AbwaegenAuswertung from './AbwaegenAuswertung';
import KiDenkt from './KiDenkt';
import { CONTRACTS } from './VertragsStudio';

const VERTRAG_MERKEN = 'enpower.abwaegen.vertrag.v1';
// Fuer Screenreader lesbar, auf dem Bildschirm unsichtbar.
const NUR_VORLESEN = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
  overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0,
};

function merke(schluessel, wert) {
  try { localStorage.setItem(schluessel, wert); } catch (_) { /* nur Komfort */ }
}
function erinnere(schluessel) {
  try { return localStorage.getItem(schluessel) || ''; } catch (_) { return ''; }
}

/** Eine Sprechblase im Gespraech: eigene rechts in Navy, die der KI links. */
function Beitrag({ vonMir = false, children }) {
  return (
    <div style={{
      alignSelf: vonMir ? 'flex-end' : 'flex-start', maxWidth: '88%',
      background: vonMir ? T.navy : T.grund,
      color: vonMir ? T.flaeche : T.ink,
      border: `1px solid ${vonMir ? T.navy : T.randWeich}`,
      borderRadius: vonMir ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
      padding: '10px 13px', fontSize: T.tText, lineHeight: 1.6,
      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    }}>{children}</div>
  );
}

export default function AbwaegenStudio({ apiBase = '/api', session = null }) {
  const token = session?.access_token || '';
  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const [vertrag, setVertrag] = useState(() => erinnere(VERTRAG_MERKEN));
  const [dealEingabe, setDealEingabe] = useState('');
  const [dealId, setDealId] = useState('');
  const [sitzung, setSitzung] = useState(null);
  const [kiBereit, setKiBereit] = useState(true);
  const [laeuft, setLaeuft] = useState('');
  const [text, setText] = useState('');
  const [belege, setBelege] = useState(null);
  const [belegeLaden, setBelegeLaden] = useState(false);
  // Kommentare, die gerade getippt werden - je Punkt. Sie gehen mit jeder
  // Auswertung mit, auch wenn das Feld nie verlassen wurde.
  const [entwuerfe, setEntwuerfe] = useState({});
  // Was gerade an die KI unterwegs ist - steht sofort im Gespraech, nicht erst,
  // wenn die Antwort zurueck ist (das dauert bis zu einer Minute).
  const [unterwegs, setUnterwegs] = useState('');

  const dateiFeld = useRef(null);
  const verlaufEnde = useRef(null);
  // Zaehlt die Bewertungen: Kommt das Nachladen der Belege erst an, wenn
  // schon neu abgewogen wurde, gehoeren sie zur alten Bewertung und werden
  // verworfen.
  const bewertungsStand = useRef(0);
  // Klicks auf "Passt" und gespeicherte Kommentare laufen NACHEINANDER zum
  // Server. Gleichzeitig laesen beide denselben Stand, und der zweite
  // Schreibvorgang wuerfe den ersten weg.
  const [kette] = useState(erstelleKette);
  const offeneSpeicherungen = useRef(0);

  const abwaegung = sitzung?.abwaegung || LEERE_ABWAEGUNG;
  const vertragsLabel = CONTRACTS.find((c) => c.key === vertrag)?.label || '';

  // Adresse der Sitzung: mit Deal dessen Nummer, ohne Deal die persoenliche.
  const sitzungsParameter = abwaegenSitzungsParameter(dealId);
  const adresse = abwaegenAdresse(apiBase, dealId);

  const melde = useCallback((fehler, ersatz) => {
    const code = fehler?.response?.data?.code || '';
    if (code === 'KI_KEIN_SCHLUESSEL') {
      setKiBereit(false);
      toast.error('Für die KI ist kein Vibecode-Schlüssel hinterlegt. Ein Administrator muss ihn eintragen.');
      return;
    }
    toast.error(fehler?.response?.data?.detail || fehler?.message || ersatz);
  }, []);

  const uebernimm = useCallback((daten) => {
    if (daten?.sitzung) setSitzung(daten.sitzung);
    if (typeof daten?.kiBereit === 'boolean') setKiBereit(daten.kiBereit);
  }, []);

  // Eine Antwort gilt nur fuer die Sitzung, an die sie ging. Wird waehrenddessen
  // der Deal gewechselt, darf die spaete Antwort den neuen Stand nicht
  // ueberschreiben (Code-Review 23.09.2026).
  const aktuelleAdresse = useRef(adresse);
  useEffect(() => { aktuelleAdresse.current = adresse; }, [adresse]);
  const uebernimmFuer = useCallback((ziel, daten) => {
    if (aktuelleAdresse.current === ziel) uebernimm(daten);
  }, [uebernimm]);

  // Die Belege sind nicht gespeichert (sie veralten) - zu einer vorhandenen
  // Bewertung werden sie frisch geholt. Eigener Abruf, damit die Bewertung
  // nicht auf den Wissensdienst wartet.
  const ladeBelege = useCallback(async (istAbgebrochen = () => false) => {
    const stand = bewertungsStand.current;
    const nochAktuell = () => !istAbgebrochen() && bewertungsStand.current === stand;
    setBelegeLaden(true);
    try {
      const antwort = await axios.get(`${adresse}&belege=1`, { headers: authHeaders });
      if (nochAktuell() && Array.isArray(antwort.data?.belege)) setBelege(antwort.data.belege);
    } catch (_) {
      // Bewusst still: Die Belege sind eine Zugabe, die Bewertung steht.
    } finally {
      if (nochAktuell()) setBelegeLaden(false);
    }
  }, [adresse, authHeaders]);

  // Beim Wechsel zwischen Deal und "ohne Deal" die Sitzung neu laden. Erst
  // leeren: Sonst stuende einen Augenblick lang die Abwaegung des vorigen
  // Deals unter dem neuen.
  useEffect(() => {
    setSitzung(null);
    setBelege(null);
    setEntwuerfe({});
    setUnterwegs('');
    if (!token) return undefined;
    let abgebrochen = false;
    (async () => {
      try {
        const antwort = await axios.get(adresse, { headers: authHeaders });
        if (abgebrochen) return;
        uebernimm(antwort.data);
        // Eine gespeicherte Vertragsart des Deals hat Vorrang vor der zuletzt
        // gewaehlten - sonst waege man einen Speicherwunsch am Dach-PV-Vertrag.
        const gespeichert = antwort.data?.sitzung?.contractKey;
        if (gespeichert && CONTRACTS.some((c) => c.key === gespeichert)) setVertrag(gespeichert);
        if (antwort.data?.sitzung?.abwaegung?.bewertung?.punkte?.length) await ladeBelege(() => abgebrochen);
      } catch (fehler) {
        if (!abgebrochen) melde(fehler, 'Die Abwägung konnte nicht geladen werden.');
      }
    })();
    return () => { abgebrochen = true; setBelegeLaden(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, sitzungsParameter, token]);

  useEffect(() => {
    verlaufEnde.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [abwaegung.verlauf.length, laeuft]);

  const koerper = useCallback((nutzlast) => abwaegenKoerper({ dealId, vertrag }, nutzlast), [dealId, vertrag]);

  // Der gespeicherte Stand, frisch vom Server - nach einem Fehler die Wahrheit.
  const ladeNeu = useCallback(async () => {
    try {
      const antwort = await axios.get(adresse, { headers: authHeaders });
      uebernimmFuer(adresse, antwort.data);
      return antwort.data?.sitzung || null;
    } catch (_) {
      return null;
    }
  }, [adresse, authHeaders, uebernimmFuer]);

  // Aktionen, auf die man wartet (KI, Datei, Bitrix): sperren die Knoepfe und
  // reihen sich in dieselbe Kette wie die kleinen Speicherungen - sonst
  // schriebe eine Speicherung waehrend der KI-Runde am selben Stand vorbei.
  const sende = useCallback(async (nutzlast, kennung) => {
    setLaeuft(kennung);
    try {
      const antwort = await kette.reihe(() => axios.post(adresse, koerper(nutzlast), { headers: authHeaders }));
      uebernimmFuer(adresse, antwort.data);
      return antwort.data;
    } catch (fehler) {
      melde(fehler, 'Die Aktion ist fehlgeschlagen.');
      return null;
    } finally {
      setLaeuft('');
    }
  }, [adresse, authHeaders, kette, koerper, melde, uebernimmFuer]);

  // Kleine Speicherungen (Passt, Kommentar, Antworttext): laufen still im
  // Hintergrund, nacheinander. Angezeigt wird erst die Antwort auf die letzte
  // - fruehere enthalten spaetere Klicks noch nicht.
  const speichere = useCallback((nutzlast, beiFehler) => {
    offeneSpeicherungen.current += 1;
    return kette.reihe(async () => {
      try {
        const antwort = await axios.post(adresse, koerper(nutzlast), { headers: authHeaders });
        if (offeneSpeicherungen.current === 1) uebernimmFuer(adresse, antwort.data);
      } catch (fehler) {
        melde(fehler, 'Speichern ist fehlgeschlagen.');
        if (beiFehler) beiFehler();
      } finally {
        offeneSpeicherungen.current -= 1;
      }
    });
  }, [adresse, authHeaders, kette, koerper, melde, uebernimmFuer]);

  const offeneEntwuerfe = useCallback(
    () => offeneKommentarEntwuerfe(abwaegung.bewertung?.punkte, entwuerfe),
    [abwaegung.bewertung, entwuerfe],
  );

  const kommentarZahl = zaehleKommentare(abwaegung.bewertung?.punkte, entwuerfe);
  const letzterVonMensch = abwaegung.verlauf.at(-1)?.rolle === 'mensch';
  const kannAuswerten = kiBereit && !laeuft && Boolean(text.trim() || kommentarZahl || letzterVonMensch);

  const abwaegen = useCallback(async () => {
    const frage = text.trim();
    const kommentare = offeneEntwuerfe();
    setText('');
    setEntwuerfe({});
    setUnterwegs(unterwegsText(frage, kommentare));
    // Die alten Belege gehoeren zur alten Bewertung. Die neuen holt die Seite
    // nach der Antwort.
    bewertungsStand.current += 1;
    setBelege(null);
    setBelegeLaden(false);
    const daten = await sende({
      op: 'abwaegen', nachricht: frage, kommentare, vertragsart: vertragsLabel || vertrag,
    }, 'abwaegen');
    setUnterwegs('');
    if (daten) {
      if (daten.sitzung?.abwaegung?.bewertung?.punkte?.length) ladeBelege();
      return;
    }
    // Fehlgeschlagen. Kam die Nachricht noch an und scheiterte erst die KI,
    // steht sie samt Kommentaren im Verlauf - dann nichts zurueck ins Feld,
    // sonst ginge alles doppelt. Sonst Text und Kommentare wiederherstellen,
    // damit nichts Getipptes verloren geht.
    const stand = await ladeNeu();
    if (nachrichtAngekommen(stand, frage, kommentare)) return;
    setText((t) => t || frage);
    setEntwuerfe((e) => ({ ...kommentare, ...e }));
  }, [ladeBelege, ladeNeu, offeneEntwuerfe, sende, text, vertrag, vertragsLabel]);

  const setzeStatus = useCallback((punktId, status) => {
    setSitzung((s) => mitPunktStand(s, punktId, status));
    if (status === 'uebernommen') {
      setEntwuerfe((e) => Object.fromEntries(Object.entries(e).filter(([id]) => id !== punktId)));
    }
    // Scheitert das Speichern, gilt wieder der Stand des Servers - kein
    // zwischengemerkter, der fruehere Klicks enthalten koennte.
    speichere({ op: 'punkt-status', punktId, status }, ladeNeu);
  }, [ladeNeu, speichere]);

  const setzeEntwurf = useCallback((punktId, kommentar) => {
    setEntwuerfe((e) => ({ ...e, [punktId]: kommentar }));
  }, []);

  const kommentiere = useCallback((punktId, kommentar) => {
    speichere({ op: 'punkt-kommentar', punktId, kommentar });
  }, [speichere]);

  const verfasse = useCallback(() => {
    sende({ op: 'antwort-verfassen', vertragsart: vertragsLabel || vertrag }, 'antwort');
  }, [sende, vertrag, vertragsLabel]);

  const speichereAntwort = useCallback((fazit) => {
    speichere({ op: 'fazit', fazit });
  }, [speichere]);

  const nachBitrix = useCallback(async (fazit) => {
    if (!String(fazit || '').trim()) return;
    const daten = await sende({ op: 'fazit-nach-bitrix', fazit, dealId }, 'bitrix');
    if (daten?.gesendet) toast.success('Als Kommentar am Deal hinterlegt.');
  }, [dealId, sende]);

  const dateiWaehlen = useCallback((ereignis) => {
    const datei = ereignis.target.files?.[0];
    ereignis.target.value = '';
    if (!datei) return;
    const leser = new FileReader();
    leser.onerror = () => toast.error(`„${datei.name}" ließ sich nicht lesen.`);
    leser.onload = async () => {
      const daten = await sende(dateiNutzlast(datei, leser.result, 'abwaegung'), 'datei');
      if (daten?.datei) toast.success(`„${daten.datei.dateiname}" gelesen — mit dem Pfeil auswerten lassen.`);
    };
    leser.readAsDataURL(datei);
  }, [sende]);

  const dealSetzen = useCallback(() => {
    const nummer = dealEingabe.trim();
    if (!istDealNummer(nummer)) {
      toast.error('Eine Deal-Nummer besteht nur aus Ziffern.');
      return;
    }
    setDealId(nummer);
  }, [dealEingabe]);

  // Nach jeder Runde der KI werden die Karten neu aufgebaut - offene
  // Kommentarfelder und aufgeklappte Begruendungen gehoeren zur alten Fassung.
  const runde = abwaegung.verlauf.filter((b) => b.rolle === 'ki').length;

  // Waehrend die KI arbeitet, spricht die Blase im Gespraech.
  const fussHinweis = laeuft ? '' : hinweisZumSenden({ kommentarZahl, hatText: Boolean(text.trim()), letzterVonMensch });

  return (
    <div className="abwaegen-seite" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: T.grund }}>
      <input ref={dateiFeld} type="file" onChange={dateiWaehlen} style={{ display: 'none' }}
        accept={DATEI_ANNAHME} />

      {/* Kopfleiste - einzeilig, ohne Erklaerkasten. */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 14,
        padding: '12px 20px', background: T.flaeche, borderBottom: `1px solid ${T.randWeich}`, flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 6, alignSelf: 'center' }}>
          <Scale size={16} color={T.navy} />
          <span style={{ fontSize: T.tTitel, fontWeight: 700, color: T.navy, letterSpacing: T.spurTitel }}>Abwägen</span>
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 260 }}>
          <Ueberzeile>Vertragsart</Ueberzeile>
          <select value={vertrag} onChange={(e) => { setVertrag(e.target.value); merke(VERTRAG_MERKEN, e.target.value); }}
            style={{ ...feldStil, padding: '7px 10px', fontWeight: 600 }}>
            <option value="">— keine bestimmte —</option>
            {CONTRACTS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </label>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }} title={DEAL_ERKLAERUNG}>
          <span id="abwaegen-deal-erklaerung" style={NUR_VORLESEN}>{DEAL_ERKLAERUNG}</span>
          <Ueberzeile>Deal (freiwillig)</Ueberzeile>
          <div style={{ display: 'flex', gap: 6 }}>
            <input value={dealEingabe} onChange={(e) => setDealEingabe(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') dealSetzen(); }}
              placeholder="Deal-Nummer" inputMode="numeric" aria-describedby="abwaegen-deal-erklaerung"
              disabled={Boolean(laeuft)}
              style={{ ...feldStil, width: 140, padding: '7px 10px' }} />
            <button type="button" onClick={dealSetzen} disabled={!dealEingabe.trim() || Boolean(laeuft)} style={knopfStil()}>Laden</button>
          </div>
        </div>

        {dealId ? (
          <div style={{ alignSelf: 'center', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={markeStil(T.navy, T.rand, T.flaeche)}><Briefcase size={11} /> Deal {dealId}</span>
            <button type="button" onClick={() => { setDealId(''); setDealEingabe(''); }} title="Ohne Deal weiterarbeiten"
              aria-label="Ohne Deal weiterarbeiten" disabled={Boolean(laeuft)}
              style={{ ...knopfStil(), padding: '5px 7px' }}><XIcon size={12} /></button>
          </div>
        ) : null}

        {!kiBereit ? (
          <span style={{ ...markeStil(T.warn, T.warnRand, T.warnGrund), alignSelf: 'center' }}>Kein Vibecode-Schlüssel</span>
        ) : null}
      </div>

      <div className="abwaegen-split" style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {/* Links: das Gespraech. */}
        <section className="abwaegen-spalte" style={{
          flex: '1 1 42%', minWidth: 0, display: 'flex', flexDirection: 'column',
          background: T.flaeche, borderRight: `1px solid ${T.randWeich}`,
        }}>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 18 }}>
            {abwaegung.verlauf.length === 0 && !unterwegs && !laeuft ? (
              <div style={{ maxWidth: 460, margin: '28px auto 0', textAlign: 'center' }}>
                <div style={{ fontSize: T.tPanel, fontWeight: 700, color: T.navy, letterSpacing: T.spurTitel }}>Was möchte der Kunde?</div>
                <div style={{ fontSize: T.tText, color: T.muted, lineHeight: 1.6, marginTop: 8 }}>
                  Füg die Mail, das Gesprächsprotokoll oder die Wünsche unten ein — gern alles auf einmal —
                  oder häng das Schreiben an.
                </div>
                {!vertrag ? (
                  <div style={{ fontSize: T.tMeta, color: T.orangeTinte, fontWeight: 600, marginTop: 14 }}>
                    Wähl oben die Vertragsart — dann prüft die KI jeden Punkt am Vertrag.
                  </div>
                ) : null}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {abwaegung.verlauf.map((b, i) => <Beitrag key={i} vonMir={b.rolle === 'mensch'}>{b.text}</Beitrag>)}
                {unterwegs ? <Beitrag vonMir>{unterwegs}</Beitrag> : null}
                {laeuft === 'datei' ? <KiDenkt schritte={['Datei wird gelesen …']} /> : null}
                {laeuft === 'abwaegen' ? <KiDenkt schritte={ABWAEGEN_SCHRITTE} /> : null}
                <div ref={verlaufEnde} />
              </div>
            )}
          </div>

          <div style={{ borderTop: `1px solid ${T.randWeich}`, padding: 14, background: T.grund, flexShrink: 0 }}>
            <div style={{ display: 'flex', gap: 7, alignItems: 'flex-end' }}>
              <button type="button" onClick={() => dateiFeld.current?.click()} disabled={Boolean(laeuft)}
                title="Kundenschreiben anhängen — PDF, Bild, Text oder Mail"
                style={{ ...knopfStil(), padding: '9px 10px', flexShrink: 0 }}>
                {laeuft === 'datei' ? <FileUp size={14} /> : <Paperclip size={14} />}
              </button>
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} disabled={!kiBereit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (kannAuswerten) abwaegen(); }
                }}
                aria-label="Nachricht an die KI"
                placeholder={abwaegung.verlauf.length === 0 && !unterwegs && !laeuft
                  ? 'Etwa: Kunde will 400 m² mehr Fläche, 25 statt 20 Jahre Laufzeit und Zutritt nur nach 48 h Anmeldung.'
                  : 'Neue Wünsche, Rückfrage oder Widerspruch …'}
                style={{ ...feldStil, resize: 'vertical', opacity: kiBereit ? 1 : 0.5 }} />
              <button type="button" onClick={abwaegen} title="Auswerten lassen (Enter)"
                disabled={!kannAuswerten}
                style={{ ...primaerKnopfStil, padding: '9px 11px', flexShrink: 0, opacity: kannAuswerten ? 1 : 0.4 }}>
                <Send size={14} />
              </button>
            </div>
            {fussHinweis ? (
              <div style={{ fontSize: T.tMikro, color: T.orangeTinte, fontWeight: 600, marginTop: 8 }}>{fussHinweis}</div>
            ) : null}
          </div>
        </section>

        {/* Rechts: die Auswertung - Offen, Uebernommen, Antwort. */}
        <section className="abwaegen-spalte" style={{ flex: '1 1 58%', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <AbwaegenAuswertung
            abwaegung={abwaegung}
            belege={belege}
            belegeLaden={belegeLaden}
            laeuft={laeuft}
            kiBereit={kiBereit}
            dealId={dealId}
            runde={runde}
            entwuerfe={entwuerfe}
            kommentarZahl={kommentarZahl}
            onEntwurf={setzeEntwurf}
            onStatus={setzeStatus}
            onKommentar={kommentiere}
            onKommentareSenden={abwaegen}
            onVerfassen={verfasse}
            onFazitSpeichern={speichereAntwort}
            onNachBitrix={nachBitrix}
          />
        </section>
      </div>
    </div>
  );
}
